import React, { useRef, useCallback, MouseEvent } from "react"
import { PrevButton } from "../Button/PrevButton"
import { FilterChooser, FilterChooserProps } from "../Button/FilterChooser"
import { SortChooser, SortChooserProps } from "../Button/SortChooser"
import { NextButton } from "../Button/NextButton"
import { useHotKey, Button } from "dash-styled"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"

interface ModalPanelNavHeaderProps<M> {
    mode: "selected" | "all"
    cursor?: { foundAt: number; totalCount: number }
    sort?: Omit<SortChooserProps<M>, "className">
    filter?: Omit<FilterChooserProps<M>, "className">
    onClearSelection?: () => void
    onPrev?: () => void
    onNext?: () => void
    containerClassName?: string
    onClose?: () => void
    disableSortAndFilter?: boolean
}

export function ModalPanelNavHeader<M>({
    mode,
    onPrev,
    onNext,
    onClearSelection,
    cursor,
    sort,
    filter,
    containerClassName,
    onClose,
    disableSortAndFilter
}: ModalPanelNavHeaderProps<M>) {
    const focusRef = useRef<HTMLDivElement>(null)
    const handleClose = useCallback(
        (evt: MouseEvent<HTMLDivElement>) => {
            if (evt.target === focusRef.current) {
                onClose?.()
            }
        },
        [onClose]
    )
    const chooserClassNameClosed =
        "transition-none ml-2 bg-transparent text-rhino-250 shadow-none rounded"
    const chooserClassNameOpen =
        "transition-none ml-2 bg-rhino-100 text-smoke-610 shadow-none rounded"
    const text = mode === "selected" ? "Selected data point" : "Data point"

    useHotKey("ArrowRight", onNext)
    useHotKey("ArrowLeft", onPrev)

    return (
        <div
            className={
                "text-sm w-full absolute left-0 top-0 h-16 flex justify-center items-center z-20 " +
                containerClassName
            }
            ref={focusRef}
            onClick={handleClose}
        >
            <PrevButton
                className={onPrev === undefined ? "opacity-25" : ""}
                onClick={onPrev}
                disabled={onPrev === undefined}
                testId="prev-button"
                aria-label="previous data point"
            />
            <div className="flex items-center px-4">
                <div className="text-base text-rhino-250 mr-2">
                    {cursor === undefined
                        ? `${text} - of -`
                        : cursor.totalCount === 0
                        ? "No matching data points"
                        : `${text} ${cursor.foundAt + 1} of ${
                              cursor.totalCount
                          }`}
                </div>
                {mode === "selected" ? (
                    onClearSelection === undefined ? null : (
                        <Button
                            testId="modal-clear-selection"
                            variant="contained2"
                            color="plain"
                            size="xs"
                            className={chooserClassNameClosed}
                            onClick={onClearSelection}
                        >
                            Clear selection
                        </Button>
                    )
                ) : (
                    <>
                        {sort === undefined ||
                        (cursor !== undefined &&
                            cursor.totalCount === 0) ? null : (
                            <SortChooser
                                {...sort}
                                testId="modal-sort-chooser"
                                classNameClosed={chooserClassNameClosed}
                                classNameOpen={chooserClassNameOpen}
                                disabled={disableSortAndFilter}
                            />
                        )}
                        {filter === undefined ? null : (
                            <FilterChooser
                                {...filter}
                                testId="modal-filter-chooser"
                                classNameClosed={chooserClassNameClosed}
                                classNameOpen={chooserClassNameOpen}
                                disabled={disableSortAndFilter}
                            />
                        )}
                    </>
                )}
            </div>
            <NextButton
                className={onNext === undefined ? "opacity-25" : ""}
                onClick={onNext}
                disabled={onNext === undefined}
                testId="next-button"
                aria-label="next data point"
            />
            {onClose && (
                <button
                    onClick={onClose}
                    className="absolute right-0 p-2 m-3 self-center"
                    data-testid="close-button"
                    aria-label="close data point"
                >
                    <IconClose className="h-6 w-6 text-white" />
                </button>
            )}
        </div>
    )
}
